import React from 'react';
import AuthService from './authService.js';
import TpClientInvoiceItem from '../Domain/TpClientInvoiceItem.js';
import TpClientInvoiceReducedListItem from '../Domain/TpClientInvoiceReducedListItem.js';
import SearchParams from '../Elements/SearchParams';
import TpClientInvoiceSearchParams from '../Contents/Operations/TpClientInvoice/TpClientInvoiceSearchParams';
import TpClientPaymentTpClientInvoiceSearchParams from '../Contents/ThirdParty/TpClientPayment/TpClientPaymentTpClientInvoiceSearchParams';
import TpClientPaymentAppliedReportSearchParams from '../Contents/ThirdParty/TpClientPayment/TpClientPaymentAppliedReportSearchParams';

export default class TpClientInvoiceService extends React.Component {
  static GetListBySearching(orderById, orderDir, pageNumber, numberOfElements, searchItem, clearCache = false) {
    const searchParams = new SearchParams(
      searchItem,
      TpClientInvoiceSearchParams,
      orderById,
      orderDir,
      pageNumber,
      numberOfElements
    );
    return AuthService.getInstance().CallBackendFunction(
      `/api/TpClientInvoice/GetListBySearchingAsync?searchParams=${searchParams.serializeForBackendCall()}&clearCache=${clearCache}`
    );
  }

  static GetOutstandingListBySearching(
    orderById,
    orderDir,
    pageNumber,
    numberOfElements,
    searchItem,
    clearCache = false
  ) {
    const searchParams = new SearchParams(
      searchItem,
      TpClientPaymentTpClientInvoiceSearchParams,
      orderById,
      orderDir,
      pageNumber,
      numberOfElements
    );
    return AuthService.getInstance().CallBackendFunction(
      `/api/TpClientInvoice/GetOutstandingListBySearchingAsync?searchParams=${searchParams.serializeForBackendCall()}&clearCache=${clearCache}`
    );
  }

  static GetAppliedListBySearching(orderById, orderDir, pageNumber, numberOfElements, searchItem, clearCache = false) {
    const searchParams = new SearchParams(
      searchItem,
      TpClientPaymentAppliedReportSearchParams,
      orderById,
      orderDir,
      pageNumber,
      numberOfElements
    );
    return AuthService.getInstance().CallBackendFunction(
      `/api/TpClientInvoice/GetAppliedListBySearchingAsync?searchParams=${searchParams.serializeForBackendCall()}&clearCache=${clearCache}`
    );
  }

  static ConvertDBItems(items) {
    const convertedItems = [];
    if (items !== null) {
      items.forEach((item) => {
        const newItem = new TpClientInvoiceItem(item);
        convertedItems.push(newItem);
      });
    }

    return convertedItems;
  }

  static ConvertReducedDBItems(items) {
    const convertedItems = [];
    if (items !== null) {
      items.forEach((item) => {
        const newItem = new TpClientInvoiceReducedListItem(item);
        convertedItems.push(newItem);
      });
    }

    return convertedItems;
  }

  static EditInvoice(item) {
    const searchItem = item.JobSearchParameters ? item.JobSearchParameters : {};
    const url = `/api/TpClientInvoice/UpsertAsync/?searchParams=${JSON.stringify(searchItem)}`;

    // The contact list only feeds the form's picker, and the job search travels in the URL.
    const { ClientContacts, JobSearchParameters, ...invoice } = item;
    invoice.AllItemsInTheSearchSetAreSelected = item.AllItemsInTheSearchSetAreSelected === true;

    return AuthService.getInstance().CallBackendFunctionPost(url, JSON.stringify(invoice));
  }

  static CancelInvoice(id, comment) {
    return AuthService.getInstance().CallBackendFunctionPut(
      `api/TpClientInvoice/CancelAsync/${id}`,
      JSON.stringify(comment)
    );
  }

  static FinalizeInvoice(editedItem) {
    return AuthService.getInstance().CallBackendFunctionPut(
      `api/TpClientInvoice/FinalizeAsync`,
      JSON.stringify(editedItem)
    );
  }

  static FinalizeSelectedInvoice(editedItem, searchItem, selected, allItemsInTheSearchSetAreSelected) {
    const url = `api/TpClientInvoice/BulkFinalizeAsync/?searchParams=${JSON.stringify(searchItem)}`;

    if (selected) {
      editedItem.TpClientInvoiceIds = selected.map((s) => s.TpClientInvoiceId);
    }
    editedItem.AllItemsInTheSearchSetAreSelected = allItemsInTheSearchSetAreSelected;

    return AuthService.getInstance().CallBackendFunctionPut(url, JSON.stringify(editedItem));
  }

  // CD-5483: the note of the finalize pop-ups for the selected invoices, every invoice of the search, or the ids typed
  // into Invoice Bulk Finalization - empty when no evaluator payment of them would need supervisor approval.
  // Information only, so a failed request is an empty note too.
  static GetFinalizeApprovalNote(searchItem, selected, allItemsInTheSearchSetAreSelected, idListString = '') {
    const url = `api/TpClientInvoice/GetFinalizeApprovalNoteAsync/?searchParams=${JSON.stringify(searchItem)}`;
    const noteRequest = {
      TpClientInvoiceIds: selected.map((s) => s.TpClientInvoiceId),
      AllItemsInTheSearchSetAreSelected: allItemsInTheSearchSetAreSelected,
      IdListString: idListString,
    };

    return AuthService.getInstance()
      .CallBackendFunctionPut(url, JSON.stringify(noteRequest))
      .then(
        (result) => (result && !result.error && result.note) || '',
        () => ''
      );
  }

  static ExportInvoicePdfs(item, searchItem, selected, allItemsInTheSearchSetAreSelected) {
    if (selected) {
      item.TpClientInvoiceIds = selected.map((s) => s.TpClientInvoiceId);
    }
    item.AllItemsInTheSearchSetAreSelected = allItemsInTheSearchSetAreSelected;

    const url = `/api/TpClientInvoice/PdfExportAsync/?searchParams=${JSON.stringify(searchItem)}`;
    return AuthService.getInstance().CallBackendFunctionPost(url, JSON.stringify(item));
  }

  static ExportAdminExcelAsync(searchItem) {
    const url = `/api/TpClientInvoice/ExcelExportAsync?searchParams=${JSON.stringify(searchItem)}&clearCache=true`;
    return AuthService.getInstance().CallBackendFunctionPost(url);
  }

  static GetById(id) {
    return AuthService.getInstance().CallBackendFunction(`/api/TpClientInvoice/GetByIdAsync/${id ? id : 0}`);
  }

  // The shops on the invoice that no longer pass the Ready for Invoicing rules, each with why; read fresh every time.
  static GetJobsNoLongerReady(id) {
    return AuthService.getInstance().CallBackendFunction(
      `/api/TpClientInvoice/GetJobsNoLongerReadyAsync/${id}?clearCache=true`
    );
  }

  static AttachReportAsync(id) {
    const url = `/api/TpClientInvoice/AttachReportAsync/${id}`;

    return AuthService.getInstance().CallBackendFunctionPut(url);
  }

  static GetInvoicePdfById(item) {
    return AuthService.getInstance().CallBackendFunction(
      `/api/TpClientInvoice/GetPdfByIdAsync/?searchParams=${JSON.stringify(item)}&clearCache=true`
    );
  }

  static AddToInvoice(addItem, searchItem, selected, allItemsInTheSearchSetAreSelected) {
    if (selected) {
      addItem.TpJobIds = selected.map((s) => s.TpJobId);
    }
    addItem.AllItemsInTheSearchSetAreSelected = allItemsInTheSearchSetAreSelected;

    return AuthService.getInstance().CallBackendFunctionPost(
      `/api/TpClientInvoice/AddTpJobToInvoiceAsync?searchParams=${JSON.stringify(searchItem)}`,
      JSON.stringify(addItem)
    );
  }

  static RemoveLines(removeItem, allItemsInTheSearchSetAreSelected) {
    removeItem.AllItemsInTheSearchSetAreSelected = allItemsInTheSearchSetAreSelected;
    return AuthService.getInstance().CallBackendFunctionPost(
      '/api/TpClientInvoice/RemoveLineAsync',
      JSON.stringify(removeItem)
    );
  }

  static GetListByClientId(clientId, filterToFinalized) {
    return AuthService.getInstance().CallBackendFunction(
      `/api/TpClientInvoice/GetListByClientIdAsync?clientId=${encodeURIComponent(
        clientId
      )}&filterToFinalized=${filterToFinalized}&clearCache=true`
    );
  }

  static GetSimpleListBySearching(orderById, orderDir, pageNumber, numberOfElements, searchItem, clearCache = false) {
    const searchParams = new SearchParams(
      searchItem,
      TpClientInvoiceSearchParams,
      orderById,
      orderDir,
      pageNumber,
      numberOfElements
    );
    return AuthService.getInstance().CallBackendFunction(
      `/api/TpClientInvoice/GetSimpleListBySearchingAsync?searchParams=${searchParams.serializeForBackendCall()}&clearCache=${clearCache}`
    );
  }

  static GetSimpleById(id) {
    return AuthService.getInstance().CallBackendFunction(`/api/TpClientInvoice/GetSimpleByIdAsync/${id ? id : 0}`);
  }

  static ExportTpRevenueToExcelAsync(searchItem) {
    const url = `/api/TpClientInvoice/ExportTpRevenueToExcelAsync?searchParams=${JSON.stringify(
      searchItem
    )}&clearCache=true`;
    return AuthService.getInstance().CallBackendFunctionPost(url);
  }
}
